type FixedZoneAddress = {
    orderId?: string
    index?: number
    anchor?: 'start' | 'end'
    hasArrived?: boolean
}

function addressKey(address: FixedZoneAddress) {
    return `${address.orderId}_${address.index}`
}

// Checks whether a reordered route still starts with the stops anchored at the start and ends with the stops anchored at
// the end, both in their original order. Used to keep a messenger from moving stops into, out of or within the fixed zone.
function keepsFixedZone(original: FixedZoneAddress[], changed: FixedZoneAddress[]) {
    const todo = (addresses: FixedZoneAddress[]) => addresses.filter((address) => !address.hasArrived)

    const startKeys = todo(original).filter((address) => address.anchor === 'start').map(addressKey)
    const endKeys = todo(original).filter((address) => address.anchor === 'end').map(addressKey)
    const changedKeys = todo(changed).map(addressKey)

    const sameKeys = (keys: string[], otherKeys: string[]) => keys.length === otherKeys.length && keys.every((key, index) => key === otherKeys[index])

    return sameKeys(changedKeys.slice(0, startKeys.length), startKeys) && sameKeys(changedKeys.slice(changedKeys.length - endKeys.length), endKeys)
}

// Gives the stop a messenger dragged to another place the anchor of the zone it was dropped in, the other stops keep theirs.
// The boundaries of the fixed zone stay between the same stops, so a stop dropped between anchored stops joins that part of
// the fixed zone and an anchored stop dragged into the flexible part is released.
function reanchor<T extends FixedZoneAddress>(original: FixedZoneAddress[], changed: T[]): T[] {
    const anchors = new Map(original.map((address) => [addressKey(address), address.anchor]))
    const withAnchors = changed.map((address) => ({...address, anchor: anchors.get(addressKey(address))}))

    const todo = withAnchors.filter((address) => !address.hasArrived)
    const keys = todo.map(addressKey)
    const originalKeys = original.filter((address) => !address.hasArrived).map(addressKey)

    // A drag moves a single stop: either the first stop out of place was moved up, or the stop that was there was moved down
    const firstChange = keys.findIndex((key, index) => key !== originalKeys[index])
    const without = (list: string[], key: string) => list.filter((otherKey) => otherKey !== key).join()
    const movedKey = [keys[firstChange], originalKeys[firstChange]].find((key) => key && without(keys, key) === without(originalKeys, key))

    if (firstChange === -1 || !movedKey) {
        return withAnchors
    }

    const index = keys.indexOf(movedKey)
    const previousAnchor = index > 0 ? todo[index - 1].anchor : 'start'
    const nextAnchor = index < todo.length - 1 ? todo[index + 1].anchor : 'end'

    // A stop is only part of a zone when it is dropped between two stops of that zone, or between the zone and the start/end of the route
    const anchor = previousAnchor === 'start' && nextAnchor === 'start' ? 'start' : previousAnchor === 'end' && nextAnchor === 'end' ? 'end' : undefined

    return withAnchors.map((address) => addressKey(address) === movedKey ? {...address, anchor} : address)
}

export default {
    addressKey,
    keepsFixedZone,
    reanchor
}
